import React from 'react';
import { Box, Typography } from '@mui/material';

function formatCost(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '$0';
  return `$${amount.toFixed(6)}`;
}

function providerLabel(step) {
  if (step?.provider === 'aws-translate') return 'Amazon Translate';
  if (step?.provider === 'cache') return 'Translation cache';
  return step?.model || 'Language model';
}

function languageSide(name, code) {
  if (name && code && name !== code) return `${name} (${code})`;
  return name || code || '?';
}

function failedStepLabel(step) {
  if (!step?.failed) return null;
  if (step.provider === 'aws-translate' || step.fallback === 'llm') {
    return 'Amazon Translate failed; used language model fallback';
  }
  return 'Translation failed';
}

function stepSummary(step) {
  const direction = step.direction === 'from_english' ? 'From English' : 'To English';
  const pair = `${languageSide(step.source, step.sourceCode)} → ${languageSide(step.target, step.targetCode)}`;
  const bits = [
    `${direction}: ${pair}`,
    providerLabel(step),
    failedStepLabel(step),
    step.cache === 'hit' ? 'cache hit' : null,
    `${step.duration_ms}ms`,
    formatCost(step.cost),
    step.tokens ? `${step.tokens} tokens` : null,
    step.provider === 'aws-translate' && step.characters != null ? `${step.characters} characters` : null,
  ].filter(Boolean);
  return bits.join(' · ');
}

/** Expandable detail under the Translation chip. */
export default function TranslationTimingBreakdown({ detail }) {
  const steps = Array.isArray(detail?.steps) ? detail.steps : [];
  if (steps.length === 0) return null;
  const providers = [...new Set(steps.map(providerLabel))];
  const callLabel = steps.length === 1 ? '1 call' : `${steps.length} calls`;
  const phaseMs = Number(detail.phase_ms);
  const showPhase = Number.isFinite(phaseMs) && phaseMs > 0 && phaseMs !== detail.duration_ms;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}>
        {providers.join(', ')}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55, mb: 0.25 }}>
        {`${callLabel} · ${detail.duration_ms}ms · ${formatCost(detail.cost)}`}
        {showPhase ? ` · inbound wait ${phaseMs}ms` : ''}
      </Typography>
      {steps.map((step, index) => (
        <Typography
          key={`${step.direction}-${index}`}
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ lineHeight: 1.55, opacity: step.failed ? 0.72 : 1 }}
        >
          {stepSummary(step)}
        </Typography>
      ))}
    </Box>
  );
}
